import { Contractor } from '@umss/shared/data-access';
import { ScopeOfPracticeEnum } from '@umss/shared/utils';

/**
 * Mapper class for transforming Contractor domain models to view-specific DTOs.
 */
export class ContractorMapper {
  /**
   * Maps a Contractor to a table/card row view model.
   * Used in contractors list/grid views.
   */
  static toTableRow(contractor: Contractor): ContractorTableRowDto {
    const currentReview = contractor.reviews?.find(
      (review) => review.scopeOfPractice === contractor.scopeOfPractice
    );

    return {
      id: contractor.id,
      name: this.getFullName(contractor),
      profilePictureUrl: contractor.profilePictureUrl || undefined,
      scopeOfPractice: this.getScopeOfPractice(contractor),
      phoneNumber: contractor.user?.phone || '',
      state: contractor.location?.stateCode || '',
      distance: contractor.distanceMiles
        ? `${contractor.distanceMiles.toFixed(1)} mi`
        : '0 mi',
      averageRating: currentReview?.score ?? 0,
      reviewCount: currentReview?.count ?? 0,
      isFavorite: contractor.isFavorite
    };
  }

  /**
   * Maps a Contractor to a profile view model.
   * Used in contractor profile modal/detail views.
   */
  static toProfile(contractor: Contractor): ContractorProfileDto {
    return {
      id: contractor.id,
      name: this.getFullName(contractor),
      profilePictureUrl: contractor.profilePictureUrl || undefined,
      scopeOfPractice: contractor.scopeOfPractice,
      rating: contractor.averageRating ?? 0,
      reviewCount: contractor.reviewCount,
      location: this.getLocation(contractor),
      distance: contractor.distanceMiles
        ? `${contractor.distanceMiles.toFixed(1)} mi`
        : '0 mi',
      email: contractor.user?.email || '',
      phoneNumber: contractor.user?.phone || '',
      notes: contractor.bio || '',
      availability: [], // Populated by component after fetching from availability endpoint
      bookedDates: [] // Populated by component after fetching shifts
    };
  }

  /**
   * Extracts full name from contractor user data.
   * Falls back to firstName + lastName if fullName is not available.
   */
  private static getFullName(contractor: Contractor): string {
    return (
      contractor.user?.fullName ||
      `${contractor.user?.firstName || ''} ${
        contractor.user?.lastName || ''
      }`.trim()
    );
  }

  /**
   * Gets the scope of practice display value.
   * Prefers display value over enum toString.
   */
  private static getScopeOfPractice(contractor: Contractor): string {
    return (
      contractor.scopeOfPracticeDisplay ||
      contractor.scopeOfPractice?.toString() ||
      ''
    );
  }

  /**
   * Formats location as "City, StateCode".
   * Returns empty string if location is not available.
   */
  private static getLocation(contractor: Contractor): string {
    if (!contractor.location) {
      return '';
    }

    const city = contractor.location.city || '';
    const state = contractor.location.stateCode || '';

    return `${city}, ${state}`.trim().replace(/^,\s*|,\s*$/g, '');
  }
}

/**
 * DTO for contractor table/card row view.
 */
export interface ContractorTableRowDto {
  id: string;
  name: string;
  profilePictureUrl?: string;
  scopeOfPractice: string;
  phoneNumber: string;
  state: string;
  distance: string;
  averageRating: number;
  reviewCount: number;
  isFavorite: boolean;
}

/**
 * DTO for contractor profile view.
 */
export interface ContractorProfileDto {
  id: string;
  name: string;
  profilePictureUrl?: string;
  scopeOfPractice: ScopeOfPracticeEnum;
  rating: number;
  reviewCount: number;
  location: string;
  distance: string;
  email: string;
  phoneNumber: string;
  notes: string;
  availability: Date[]; // Dates when contractor is generally available (from weekly schedule)
  bookedDates: Date[]; // Dates when contractor has confirmed shifts
}
